import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { appeler, ErreurApi } from "../api/client";
import { useApi } from "../api/hooks";
import { EtatChargement, EtatErreur } from "../composants/Avis";
import { ListeConfigSchema, type EntreeConfig } from "../api/schemas";

type Ligne = { id: string | null; libelle: string; actif: boolean; cle: string | null };

/**
 * Une liste ordonnée, éditable — types de dossiers ou natures de pièces.
 *
 * ⛔ ON NE SUPPRIME PAS, ON DÉSACTIVE.
 *
 * Les pièces déjà classées portent le libellé du type DANS LEUR CHEMIN.
 * Supprimer la ligne ne déplacerait aucun fichier : elle rendrait seulement
 * leur emplacement inexplicable. Une entrée retirée cesse d'être proposée ; ce
 * qui est dessous reste lisible.
 */
function ListeEditable({
  titre,
  resume,
  chemin,
  cle,
  cleVerrouillee,
}: {
  titre: string;
  resume: string;
  chemin: string;
  cle: string;
  cleVerrouillee: boolean;
}) {
  const q = useApi([cle], chemin, ListeConfigSchema);

  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (q.data) setLignes(q.data.map(depuis));
  }, [q.data]);

  function depuis(e: EntreeConfig): Ligne {
    return { id: e.id, libelle: e.libelle, actif: e.actif, cle: e.cle };
  }

  function bouger(i: number, pas: number) {
    const j = i + pas;
    if (j < 0 || j >= lignes.length) return;
    const copie = [...lignes];
    [copie[i], copie[j]] = [copie[j], copie[i]];
    setLignes(copie);
  }

  function modifier(i: number, champ: Partial<Ligne>) {
    setLignes(lignes.map((l, k) => (k === i ? { ...l, ...champ } : l)));
  }

  async function enregistrer() {
    setErreur(null);
    setAvis(null);
    setEnCours(true);
    try {
      const r = await appeler(chemin, ListeConfigSchema, {
        method: "PUT",
        body: JSON.stringify({
          types: lignes.map((l) => ({ id: l.id, libelle: l.libelle, actif: l.actif })),
        }),
      });
      setLignes(r.map(depuis));
      setAvis("Enregistré. La modification vaut pour tous les clients.");
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? [e.message, e.motif].filter(Boolean).join(" ") : "Échec.",
      );
    } finally {
      setEnCours(false);
    }
  }

  if (q.isLoading) return <EtatChargement quoi="de la configuration" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  return (
    <section>
      <h2>{titre}</h2>
      <p className="resume">{resume}</p>

      <table className="tableau">
        <thead>
          <tr>
            <th>Ordre</th>
            <th>Libellé</th>
            <th>Proposé au dépôt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={l.id ?? `nouveau-${i}`}>
              <td>
                <button type="button" onClick={() => bouger(i, -1)} disabled={i === 0}>↑</button>
                <button
                  type="button"
                  onClick={() => bouger(i, 1)}
                  disabled={i === lignes.length - 1}
                >
                  ↓
                </button>
              </td>
              <td>
                <input
                  value={l.libelle}
                  onChange={(e) => modifier(i, { libelle: e.target.value })}
                />
                {cleVerrouillee && l.cle && (
                  <span className="note" title="Cible du classement automatique. Non modifiable.">
                    {" "}clé : {l.cle}
                  </span>
                )}
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={l.actif}
                  onChange={(e) => modifier(i, { actif: e.target.checked })}
                />
              </td>
              <td>
                {/* Décocher « proposé au dépôt » EST le retrait. Pas de
                    suppression : voir le commentaire en tête de fichier. */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="depot__actions">
        <button
          type="button"
          className="bouton-creux"
          onClick={() => setLignes([...lignes, { id: null, libelle: "", actif: true, cle: null }])}
        >
          + Ajouter
        </button>
        <button
          type="button"
          className="bouton-plein"
          onClick={() => void enregistrer()}
          disabled={enCours}
        >
          {enCours ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {avis && <p className="note">{avis}</p>}
      {erreur && <p className="alerte" role="alert">{erreur}</p>}
    </section>
  );
}

/**
 * §5.B — La configuration DU CABINET, commune à tous les clients.
 *
 * Elle ne se règle pas dossier par dossier : deux clients qui classeraient
 * différemment rendraient toute revue transversale impossible, et le classement
 * automatique n'aurait plus de cible commune.
 */
export function Configuration() {
  return (
    <main className="liste-dossiers">
      <header className="entete">
        <div className="entete__logo">MDC</div>
        <div>
          <h1>Configuration du cabinet</h1>
          <div className="entete__sous-titre">
            Ces réglages valent pour TOUS les clients
          </div>
        </div>
        <div className="entete__moi">
          <Link className="bouton-creux" to="/dossiers">Retour aux dossiers</Link>
        </div>
      </header>

      <div className="contenu">
        <ListeEditable
          titre="Types de dossiers documentaires"
          resume={
            "Le premier niveau sous un mois. Le client dépose DEDANS et crée ses " +
            "sous-dossiers à l'intérieur ; il n'en crée jamais. Le libellé est " +
            "modifiable, mais la clé — que vise le classement automatique — ne " +
            "l'est pas : sans elle, renommer un type enverrait les pièces classées " +
            "automatiquement dans un dossier inexistant."
          }
          chemin="/configuration/types-documentaires"
          cle="types-documentaires"
          cleVerrouillee
        />

        <ListeEditable
          titre="Natures de pièces"
          resume={
            "Ce qu'une pièce EST — reçu, facture d'achat, facture de vente, " +
            "inventaire, mouvement de stock… — par opposition à l'endroit où elle " +
            "est rangée. Elle est déclarée au dépôt, par celui qui dépose : lui " +
            "seul sait ce qu'il tient en main."
          }
          chemin="/configuration/natures-pieces"
          cle="natures-pieces"
          cleVerrouillee={false}
        />
      </div>
    </main>
  );
}
